import { useEffect, useRef, useState } from "react";
import {
  Activity,
  ClipboardList,
  Monitor,
  Network,
  Scale,
  Users,
} from "lucide-react";
import { Bubble } from "@/components/diagrams/Bubble";
import { MissionControlDefs } from "@/components/diagrams/MissionControlDefs";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import type { BubbleConfig } from "@/components/diagrams/types";
import {
  WORKSHOP_TO_BUBBLE,
  useBubbleWorkshopLink,
} from "@/stores/useBubbleWorkshopLink";

/**
 * MC_00 — Mission Control capability ring. Six capability bubbles in a
 * 3 × 2 grid with cross-connecting dashed lines.
 *
 * Layout uses CSS Grid to give the "XAES Operating Model" wordmark its
 * own column on the left, separate from the SVG bubble area on the
 * right. The vanilla did this with absolute positioning + a left-shifted
 * viewBox — fragile under aspect-ratio changes. The grid version stays
 * stable across viewport sizes.
 *
 * All six bubbles share the same peach fill + stroke per legacy
 * `_legacy/mc.html` lines 1559-1621 (every `<circle>` uses
 * `fill="url(#bYellow00)" stroke="#FFC982"`). The multi-colour gradient
 * defs in `MissionControlDefs` are for other panels — MC_00 itself is
 * monochrome.
 */

const BUBBLE_ACCENTS = [
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
  { gradId: "bYellow00", stroke: "var(--accent-peach)" },
] as const;

const CAPABILITY_BUBBLES: BubbleConfig[] = [
  {
    id: "c1",
    cx: 200,
    cy: 108,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[0].gradId})`,
    stroke: BUBBLE_ACCENTS[0].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 191,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["STRATEGY &", "DELIVERY MODEL"],
    bodyLines: [
      "Aligning Capabilities",
      "to Outcomes.",
      "Scaling Agentic",
      "Enterprise.",
    ],
    icon: ClipboardList,
    tipTitle: "Strategy & Delivery Model",
    tipBody: [
      "Sets the AI transformation vision and strategic objectives",
      "Aligns capability investment to business outcomes",
      "Sequences delivery so capabilities scale enterprise-wide, not just isolated pilots",
    ],
  },
  {
    id: "c2",
    cx: 360,
    cy: 108,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[1].gradId})`,
    stroke: BUBBLE_ACCENTS[1].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 351,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["PROCESS &", "WORKFLOW"],
    bodyLines: ["End-to-End Work", "Redesign.", "Value Realisation."],
    icon: Network,
    tipTitle: "Process & Workflow",
    tipBody: [
      "Redesigns end-to-end workflows to integrate AI agents",
      "Maps human/agent handoffs so automation targets highest-value steps",
      "Removes friction for continuous, intelligent process execution",
      "Tracks value realisation as workflows mature",
    ],
  },
  {
    id: "c3",
    cx: 520,
    cy: 108,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[2].gradId})`,
    stroke: BUBBLE_ACCENTS[2].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 511,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["GOVERNANCE &", "ACCOUNTABILITY"],
    bodyLines: [
      "Decision Rights &",
      "Guardrails.",
      "Human-In-The-Loop",
      "Audit.",
    ],
    icon: Scale,
    tipTitle: "Governance & Accountability",
    tipBody: [
      "Sets policies and controls that keep AI safe, ethical, and within bounds",
      "Defines decision rights between humans and agents",
      "Enforces human-in-the-loop review at the right checkpoints",
      "Keeps an auditable trail for every autonomous action",
    ],
  },
  {
    id: "c4",
    cx: 200,
    cy: 272,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[3].gradId})`,
    stroke: BUBBLE_ACCENTS[3].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 191,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["TECHNOLOGY &", "KNOWLEDGE"],
    bodyLines: ["Reliable & Scalable", "Platforms.", "Integrated Context."],
    icon: Monitor,
    tipTitle: "Technology & Knowledge",
    tipBody: [
      "Provides the AI platforms and data infrastructure behind agent capabilities",
      "Integrates context sources so agents reason over current, trustworthy data",
      "Keeps platforms reliable and scalable beyond pilot workloads",
    ],
  },
  {
    id: "c5",
    cx: 360,
    cy: 272,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[4].gradId})`,
    stroke: BUBBLE_ACCENTS[4].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 351,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["WORKFORCE &", "CULTURE"],
    bodyLines: ["Role Shifts & Skills.", "Human-Agent", "Behaviours."],
    icon: Users,
    tipTitle: "Workforce & Culture",
    tipBody: [
      "Builds the skills and mindsets to work alongside AI agents confidently",
      "Reshapes roles as tasks shift to agents",
      "Establishes trust patterns for sustainable day-to-day human-agent collaboration",
    ],
  },
  {
    id: "c6",
    cx: 520,
    cy: 272,
    outerR: 76,
    innerR: 71,
    fill: `url(#${BUBBLE_ACCENTS[5].gradId})`,
    stroke: BUBBLE_ACCENTS[5].stroke,
    outerStrokeW: 1.5,
    outerOp: 0.75,
    ringStrokeW: 0.8,
    ringOp: 0.35,
    filter: "url(#glow00)",
    iconTx: 511,
    iconScale: 0.75,
    titleFontSize: 10,
    letterSpacing: 0.5,
    dividerHW: 36,
    bodyFontSize: 11,
    titleLines: ["PERFORMANCE &", "VALUE"],
    bodyLines: ["Business Outcomes", "Evidence.", "Trust Signals."],
    icon: Activity,
    tipTitle: "Performance & Value",
    tipBody: [
      "Tracks business value delivered by AI through measurable outcomes and ROI",
      "Surfaces trust signals stakeholders need to scale adoption",
      "Feeds evidence of what's working back into strategy decisions",
    ],
  },
];

const CONNECTORS: Array<[number, number, number, number]> = [
  [200, 108, 200, 272],
  [360, 108, 360, 272],
  [520, 108, 520, 272],
  [200, 108, 360, 108],
  [360, 108, 520, 108],
  [200, 272, 360, 272],
  [360, 272, 520, 272],
  [200, 108, 520, 272],
  [520, 108, 200, 272],
];

/**
 * Phone layout — 2 columns × 3 rows instead of 3 × 2. The desktop ring's
 * viewBox is landscape (512×356, aspect 1.44); on a portrait phone frame
 * that forces the whole block to shrink to fit the *width*, wasting most
 * of the available height and rendering bubble text unreadably small
 * (reported: "bubbles are way too small on phone"). Same six bubbles,
 * same content, just reflowed into a portrait-aspect (0.73) arrangement
 * so a tall narrow frame fills with real bubble size instead of
 * shrinking to fit width. Same reading order (c1→c6), just 2-wide
 * instead of 3-wide.
 */
const PHONE_POSITIONS: Array<{ cx: number; cy: number; iconTx: number }> = [
  { cx: 170, cy: 106, iconTx: 161 },
  { cx: 380, cy: 106, iconTx: 371 },
  { cx: 170, cy: 286, iconTx: 161 },
  { cx: 380, cy: 286, iconTx: 371 },
  { cx: 170, cy: 466, iconTx: 161 },
  { cx: 380, cy: 466, iconTx: 371 },
];

const PHONE_CAPABILITY_BUBBLES: BubbleConfig[] = CAPABILITY_BUBBLES.map(
  (bubble, i) => ({
    ...bubble,
    cx: PHONE_POSITIONS[i]!.cx,
    cy: PHONE_POSITIONS[i]!.cy,
    iconTx: PHONE_POSITIONS[i]!.iconTx,
  }),
);

const PHONE_CONNECTORS: Array<[number, number, number, number]> = [
  [170, 106, 380, 106],
  [170, 286, 380, 286],
  [170, 466, 380, 466],
  [170, 106, 170, 286],
  [170, 286, 170, 466],
  [380, 106, 380, 286],
  [380, 286, 380, 466],
  [170, 106, 380, 466],
  [380, 106, 170, 466],
];

// Bubbles span cx 170-380, outerR 76 → x 94-456, y 30-542. 20 units of
// margin on every side, same convention as VIEW_BOX_RING below.
const VIEW_BOX_RING_PHONE = "74 10 402 552";
const RING_ASPECT_PHONE = 402 / 552;
// Below this frame width the landscape (3×2) ring shrinks past legible
// bubble text — switch to the portrait (2×3) layout instead.
const PHONE_RING_MAX_WIDTH = 480;

/**
 * The legacy `_legacy/mc.html` MC_00 overlaid the wordmark on top of the
 * bubble SVG via a left-shifted viewBox (`70 15 500 350`) — the ring's own
 * viewBox reserved dead space on the left for the DOM-positioned title.
 * That coupling meant every ring-geometry tweak had to also budget wordmark
 * space, and vice versa (see git history: the outer bubbles clip the right
 * edge of that viewBox even in the legacy file). Split into a real two-column
 * layout instead — the wordmark gets its own flex column, the ring gets a
 * single, symmetric viewBox sized to its own geometry — so neither fights
 * the other for room.
 *
 * Below `WORDMARK_MIN_WIDTH` the wordmark column collapses so the ring gets
 * the full frame. Measured off the element rather than a media query: this
 * renders inside `#os-scale-root`, whose layout box is wider than the CSS
 * viewport under Windows display scaling, so viewport widths fold at the
 * wrong time.
 *
 * Ring viewBox: bubbles span cx 200-520, outerR 76 → x 124-596, y 32-348.
 * 20 units of margin on every side keeps the ring off the frame edges.
 */
const WORDMARK_MIN_WIDTH = 620;
const VIEW_BOX_RING = "104 12 512 356";

// Ring viewBox aspect (512 / 356) — used to size the block off the
// frame's actual height instead of a guessed pixel cap, so the ring
// fills tall frames instead of shrinking to fit a static max-width.
const RING_ASPECT = 512 / 356;
// Rough column width for the wordmark + its gap to the ring, used only
// to size the ideal block width — doesn't need to be pixel-exact.
// Measured rendered wordmark column ≈106px + gap-8 (32px) ≈ 138px; the old
// 200px guess over-budgeted this column, inflating idealBlockWidth and
// forcing the ring's own flex-1 box wider than its natural aspect — the
// ring's `preserveAspectRatio="xMidYMid meet"` then quietly letterboxed
// that extra width *inside* the SVG, on top of the block's own centering
// margin, splitting the horizontal breathing room into two stacked gaps
// that read as uneven next to the single clean `py-6` gap on top/bottom.
const WORDMARK_ALLOWANCE = 140;
// Below `WORDMARK_MIN_WIDTH` the wordmark moves from beside the ring to
// above it (see `isNarrow` below) instead of disappearing — Patrick
// asked for "XAES Operating Model" to stay visible on phone. Approx
// rendered height of the stacked "XAES" + "Operating"/"Model" block
// plus its gap to the ring, budgeted out of available height there.
const WORDMARK_STACK_HEIGHT = 96;
const FRAME_VERTICAL_PADDING = 48; // py-6 top + bottom
const FRAME_HORIZONTAL_PADDING = 48; // px-6 left + right
// In a near-square or portrait frame the width-bound branch below fills
// the *entire* remaining width, so the only left/right gap is `px-6`
// itself — dwarfed next to the top/bottom slack that's left over once a
// landscape ring stops growing taller than the frame is wide. Reserving
// extra width up front keeps a real, deliberate side margin instead of
// riding flush to `px-6` in that case.
const HORIZONTAL_BREATHING_ROOM = 96;

export function CapabilityRing() {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setFrameSize({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Below `WORDMARK_MIN_WIDTH` the wordmark stacks above the ring instead
  // of beside it (always visible — never hidden). Below `PHONE_RING_MAX_WIDTH`
  // the bubbles themselves reflow to the portrait 2×3 layout.
  const isNarrow = frameSize.width > 0 && frameSize.width < WORDMARK_MIN_WIDTH;
  const isPhoneRing = frameSize.width > 0 && frameSize.width < PHONE_RING_MAX_WIDTH;
  // Bubbles render ring + text only (no completion fill). A bubble lights
  // up when either it or one of its linked workshops (Program Rollup row)
  // is hovered.
  const hoveredBubbleId = useBubbleWorkshopLink((s) => s.hoveredBubbleId);
  const hoveredWorkshopId = useBubbleWorkshopLink((s) => s.hoveredWorkshopId);
  const setHoveredBubble = useBubbleWorkshopLink((s) => s.setHoveredBubble);

  const bubbles = isPhoneRing ? PHONE_CAPABILITY_BUBBLES : CAPABILITY_BUBBLES;
  const connectors = isPhoneRing ? PHONE_CONNECTORS : CONNECTORS;
  const viewBoxRing = isPhoneRing ? VIEW_BOX_RING_PHONE : VIEW_BOX_RING;
  const ringAspect = isPhoneRing ? RING_ASPECT_PHONE : RING_ASPECT;
  const guideVertical = isPhoneRing ? { x: 275, y2: 580 } : { x: 320, y2: 380 };
  const guideHorizontal = isPhoneRing ? { y: 286, x2: 460 } : { y: 190, x2: 640 };

  // Contain-fit: pick whichever axis actually constrains the block so it
  // never asks for more height OR width than the frame has. Deriving the
  // width from height alone (the old approach) assumed the frame is
  // always wide enough to absorb that width — false whenever the frame
  // is closer to square/portrait. There, the block still claimed the
  // frame's full height while its width got capped by the row's real
  // width, leaving the ring's own SVG box taller than it needed to be —
  // `preserveAspectRatio` then quietly letterboxed that *inside* the
  // SVG (a hidden vertical gap `py-6` never accounted for) instead of
  // the shortfall showing up as ordinary, symmetric outer margin.
  // Stacked (isNarrow) layout budgets the wordmark's own height out of
  // the frame instead of its width — it sits above the ring, not beside
  // it, so it never competes with the ring for horizontal room.
  const availableHeight = Math.max(
    frameSize.height -
      FRAME_VERTICAL_PADDING -
      (isNarrow ? WORDMARK_STACK_HEIGHT : 0),
    0,
  );
  // `HORIZONTAL_BREATHING_ROOM` reserves side margin for the desktop
  // width-bound case, where the wordmark sits *beside* the ring and a
  // landscape ring would otherwise ride flush to `px-6`. Stacked
  // (isNarrow) layout has no such neighbour — the wordmark is above,
  // not beside — so reserving it there just starves the ring's own
  // width for no reason (a 295px-wide phone frame lost 96px to a gap
  // that doesn't exist in that layout, capping bubbles at ~56px).
  const availableWidth = Math.max(
    frameSize.width -
      FRAME_HORIZONTAL_PADDING -
      (isNarrow ? 0 : HORIZONTAL_BREATHING_ROOM),
    0,
  );
  const wordmarkAllowance = isNarrow ? 0 : WORDMARK_ALLOWANCE;

  let idealBlockWidth: number | undefined;
  let idealBlockHeight: number | undefined;
  if (availableHeight > 0 && availableWidth > 0) {
    const heightBoundWidth = availableHeight * ringAspect + wordmarkAllowance;
    if (heightBoundWidth <= availableWidth) {
      // Frame has room to spare horizontally — fill full height, as before.
      idealBlockWidth = heightBoundWidth;
      idealBlockHeight = availableHeight;
    } else {
      // Frame is too narrow/tall for that — fill full width instead and
      // let height fall out of the same aspect math, so the block (and
      // the ring inside it) always renders at its true, undistorted
      // size with no internal letterboxing either way.
      idealBlockWidth = availableWidth;
      idealBlockHeight =
        Math.max(availableWidth - wordmarkAllowance, 0) / ringAspect;
    }
  }

  return (
    <div
      ref={frameRef}
      className="border-true-blue/30 bg-card/70 relative h-full w-full overflow-hidden rounded-md border"
    >
      {/* Explicit peach RGB (the dark-mode `--accent-peach` hex
          255,201,130) instead of `cssVarName` — light mode's
          `--accent-peach` is `#9E6C2F` (a dim brown) which renders
          the smoke almost invisible at the 0.35 alpha multiplier. */}
      <SmokeCanvas
        className="pointer-events-none absolute inset-0 z-0 h-full w-full"
        color={[255, 201, 130]}
      />

      {/* Two real columns instead of a DOM overlay on the ring's viewBox
          (see comment above `VIEW_BOX_RING`) — the wordmark gets its own
          space, the ring never has to budget around it.
          Fixed-aspect sizing on the ring centered the whole block nicely at
          the width this was designed for, but had no floor: shrink the
          frame and the ring held its natural size and pushed the wordmark
          off the left edge. The `max-w` wrapper below caps the whole
          block's width so it centers like that at rest, while the ring
          stays `flex-1 min-w-0` (fills — and shrinks into — whatever's
          left, letterboxed by its own `preserveAspectRatio`) so the row as
          a whole can never exceed the frame. */}
      <div className="relative z-[1] flex h-full w-full items-center justify-center px-6 py-6">
        <div
          className={`flex h-full w-full max-w-[760px] ${
            isNarrow
              ? "flex-col items-center justify-center gap-3"
              : "items-stretch gap-8"
          }`}
          style={
            idealBlockWidth && idealBlockHeight
              ? isNarrow
                ? { width: `${idealBlockWidth}px`, height: "auto" }
                : {
                    maxWidth: `${idealBlockWidth}px`,
                    height: `${idealBlockHeight}px`,
                  }
              : undefined
          }
        >
        {/* Wordmark — always visible (never hidden, just repositioned).
            Beside the ring at desktop widths, stacked above it once
            `isNarrow` (see the block's own flex-direction above) so
            "XAES Operating Model" reads on every screen size. */}
        <div className="text-foreground pointer-events-none flex flex-shrink-0 flex-col items-center justify-center gap-1">
            <span
              className="font-sans font-bold uppercase whitespace-nowrap"
              style={{
                // Bumped above legacy `--fs-heading` (14px) so the wordmark
                // reads as the diagram's primary label at modern viewport sizes.
                fontSize: "var(--fs-title)",
                letterSpacing: "0.3em",
                // Cream halo to match legacy mc-panel-label text-shadow
                // (rgba(255,255,255,*)) — softens in light mode via opacity.
                textShadow:
                  "0 0 6px hsl(var(--foreground) / 0.9), 0 0 16px hsl(var(--foreground) / 0.7), 0 0 40px hsl(var(--foreground) / 0.5), 0 0 80px hsl(var(--foreground) / 0.25)",
              }}
            >
              XAES
            </span>
          </div>

        {/* Bubble diagram */}
        <svg
          viewBox={viewBoxRing}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height={isNarrow ? idealBlockHeight ?? "100%" : "100%"}
          preserveAspectRatio="xMidYMid meet"
          className="block min-w-0 flex-1"
          style={{ overflow: "hidden" }}
          role="img"
          aria-label="XAES — six capability bubbles"
        >
          <MissionControlDefs />

          {/* Subtle quadrant guides — track the foreground colour at
              low alpha so they fade with the theme. */}
          <line
            x1={guideVertical.x}
            y1={0}
            x2={guideVertical.x}
            y2={guideVertical.y2}
            stroke="hsl(var(--foreground) / 0.04)"
            strokeWidth={1}
          />
          <line
            x1={0}
            y1={guideHorizontal.y}
            x2={guideHorizontal.x2}
            y2={guideHorizontal.y}
            stroke="hsl(var(--foreground) / 0.04)"
            strokeWidth={1}
          />

          {/* Connecting lines */}
          {connectors.map(([x1, y1, x2, y2], i) => (
            <line
              key={`con-${i}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="var(--accent-peach)"
              strokeOpacity={0.15}
              strokeWidth={1}
              strokeDasharray="4,3"
            />
          ))}

          {bubbles.map((config) => (
            <Bubble
              key={config.id}
              config={config}
              highlighted={
                hoveredBubbleId === config.id ||
                (hoveredWorkshopId !== null &&
                  WORKSHOP_TO_BUBBLE[hoveredWorkshopId] === config.id)
              }
              onHoverChange={(hovering) =>
                setHoveredBubble(hovering ? config.id : null)
              }
            />
          ))}
        </svg>
        </div>
      </div>
    </div>
  );
}
